import type { AgendaReadGuard } from "@/lib/agendaQueries";
import { supabase } from "@/integrations/supabase/client";
import { agendaHasAllDayDates } from "@/lib/agendaSchema";
import { eventStartDate, type AgendaDates } from "@/lib/agendaDates";
import { localEventDayKey } from "@/lib/agendaEventRange";

export interface NextAgendaEvent extends AgendaDates {
  id: string;
  title: string;
  end_at: string | null;
  location: string | null;
  external_provider: string | null;
  source: string | null;
  tag?: string | null;
}

/** Shared by Home and Agenda. All-day UTC anchors are storage compatibility,
 * not appointment times. A date-only start today remains eligible all day. */
export async function queryNextAgendaEvent(userId: string, now = new Date(), guard?: AgendaReadGuard) {
  guard?.assertCurrent();
  const hasDates = await agendaHasAllDayDates();
  guard?.assertCurrent();
  let timed = supabase.from("agenda_events").select("*")
    .eq("user_id", userId).is("deleted_at", null).gt("start_at", now.toISOString());
  if (hasDates) timed = timed.is("all_day_start", null);
  if (guard) timed = timed.setHeader("Authorization", `Bearer ${guard.accessToken}`);
  let allDay = hasDates ? supabase.from("agenda_events").select("*")
    .eq("user_id", userId).is("deleted_at", null)
    .gte("all_day_start", localEventDayKey(now.toISOString())) : null;
  if (guard && allDay) allDay = allDay.setHeader("Authorization", `Bearer ${guard.accessToken}`);
  guard?.assertCurrent();
  const [timedResult, allDayResult] = await Promise.all([
    timed.order("start_at", { ascending: true }).limit(1).maybeSingle(),
    allDay ? allDay.order("all_day_start", { ascending: true }).limit(1).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ]);
  guard?.assertCurrent();
  const error = timedResult.error || allDayResult.error;
  // A partial query cannot establish which event is next.
  if (error) return { data: null, error };
  const candidates = [timedResult.data, allDayResult.data].filter(Boolean) as NextAgendaEvent[];
  candidates.sort((a, b) => eventStartDate(a).getTime() - eventStartDate(b).getTime());
  return { data: candidates[0] ?? null, error: null };
}
